/**
 * Mağaza pages (Satışlar, İlanlar) over GET /shop: orders with their items and the country
 * split, twelve months of revenue, and the listing table with Etsy visits, views, favourites,
 * 90 day orders, monthly visits, traffic sources and search terms, plus Etsy wide search
 * volumes. Read only. The numbers come from the Etsy reader, so an empty page says what to
 * connect instead of showing zeros as facts.
 */

import { useEffect, useId, useState } from 'react';
import {
  centsToMicro,
  listingStateLabel,
  orderStatusLabel,
  type ShopListing,
  type ShopMarketTerm,
  type ShopMonth,
  type ShopOrder,
} from '../api/shop.js';
import { Link } from '../app/router.js';
import { type ShopState, useShop } from '../app/shop.js';
import { formatDayMonth, formatNumber, formatRelativeClock, formatUsd } from '../lib/format.js';
import { EmptyState, Segmented } from '../screens/common.js';
import { BarValueLabels, ChartFrame, ChartReadout, ChartTakeaway, pickHandlers, useChartPick } from './chart-kit.js';
import { Icon } from './icons.js';
import { SectionHeader } from './primitives.js';

function useShopView(): { state: ShopState; reload: () => void } {
  const { store, state } = useShop();
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  return { state, reload: () => void store.reload() };
}

function ShopStatus({ state, reload }: { state: ShopState; reload: () => void }) {
  if (state.error !== null && state.view === null) {
    return (
      <div className="shop-status" role="status">
        Mağaza verisi yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={reload}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="shop-status" role="status">
      Yükleniyor…
    </div>
  );
}

const ConnectHint = () => (
  <p className="shop-hint">
    Etsy mağazası bağlanınca ilk okuma hemen, sonrakiler 07:00-22:00 arası üç saatte bir.{' '}
    <Link to="/ayarlar/baglanti/etsy">Ayarlar › Bağlantılar › Etsy</Link>
  </p>
);

// ---- Satışlar ---------------------------------------------------------------

/** Items have no id of their own: the key is the line itself, numbered when one repeats. */
function keyedItems(order: ShopOrder): { key: string; item: ShopOrder['items'][number] }[] {
  const seen = new Map<string, number>();
  return order.items.map((item) => {
    const base = `${item.listingId ?? ''}:${item.title}:${item.quantity}:${item.priceCents}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return { key: n === 1 ? base : `${base}#${n}`, item };
  });
}

function OrderRow({ order, open, onToggle }: { order: ShopOrder; open: boolean; onToggle: () => void }) {
  const itemsId = useId();
  const first = order.items[0];
  const more = order.items.length - 1;
  const settled = order.status === 'paid' || order.status === 'completed';
  return (
    <li className="shop-order">
      <button
        type="button"
        className="shop-order__main"
        aria-expanded={open}
        aria-controls={open ? itemsId : undefined}
        onClick={onToggle}
      >
        <span className="shop-order__day mono">{formatDayMonth(order.orderedAt)}</span>
        <span className="country mono">{order.country ?? '··'}</span>
        <span className="shop-order__title">
          {first ? first.title : `Sipariş ${order.orderId}`}
          {more > 0 ? <span className="shop-order__more"> +{more}</span> : null}
        </span>
        <span className="shop-order__amount mono">{formatUsd(centsToMicro(order.totalCents))}</span>
        <span className={settled ? 'shop-order__status' : 'shop-order__status shop-order__status--off'}>
          {orderStatusLabel(order.status)}
          {order.couponCode ? ` · ${order.couponCode}` : ''}
          {order.channel ? ` · ${order.channel}` : ''}
        </span>
      </button>
      {open ? (
        <ul id={itemsId} className="shop-order__items" aria-label={`Sipariş ${order.orderId} kalemleri`}>
          {keyedItems(order).map(({ key, item }) => (
            <li key={key} className="shop-order__item">
              <span className="mono">{item.quantity} ×</span> {item.title}
              <span className="mono shop-order__item-price">{formatUsd(centsToMicro(item.priceCents))}</span>
            </li>
          ))}
          {order.discountCents > 0 ? (
            <li className="shop-order__item shop-order__item--discount">
              indirim{' '}
              <span className="mono shop-order__item-price">-{formatUsd(centsToMicro(order.discountCents))}</span>
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Son 12 ay (audit chart rules, 25 Oct 2026): the takeaway on top, the best and the running month carry their
 * revenue on the plot, a tap, a click or the arrow keys pick a month and the readout row says it (the running month
 * until then). Bars in the neutral data colour; the running month hatched.
 */
export function MonthlyChart({ months }: { months: readonly ShopMonth[] }) {
  const pick = useChartPick(months.length);
  const values = months.map((month) => month.cents);
  const max = Math.max(1, ...values);
  const current = months.at(-1);
  const before = months.at(-2);
  const best = months
    .slice(0, -1)
    .reduce<ShopMonth | null>((top, month) => (top === null || month.cents > top.cents ? month : top), null);
  const shown = months[pick.index];
  const money = (cents: number) => formatUsd(centsToMicro(cents), 0);
  const readout =
    shown === undefined
      ? ''
      : `${shown.label} ${shown.year}${shown === current ? ' (sürüyor)' : ''}: ${money(shown.cents)} · ${shown.orders} sipariş`;
  return (
    <figure className="shop-chart">
      {current === undefined ? null : (
        <ChartTakeaway>
          {current.label} şimdiye kadar {money(current.cents)}
          {before === undefined ? '' : `, ${before.label} ${money(before.cents)}`}
          {best === null || best.cents === 0 ? '' : ` · en iyi ay ${best.label} ${best.year} (${money(best.cents)})`}
        </ChartTakeaway>
      )}
      <ChartFrame label="Son 12 ay: ay seç" count={months.length} pick={pick} valueText={readout}>
        <div className="chart-plot shop-months__plot">
          <ol className="shop-months" aria-label="Aylık ciro">
            {months.map((month, index) => (
              <li
                key={`${month.year}-${month.month}`}
                className={pick.index === index ? 'shop-month is-picked' : 'shop-month'}
                {...pickHandlers(pick, index)}
              >
                <span
                  className="shop-month__bar"
                  style={{ height: `${Math.max(2, (month.cents / max) * 100)}%` }}
                  aria-hidden="true"
                />
                <span className="sr-only">
                  {month.label} {month.year}: {formatUsd(centsToMicro(month.cents))}, {month.orders} sipariş
                </span>
              </li>
            ))}
          </ol>
          <BarValueLabels values={values} max={max} text={money} />
        </div>
        <div className="shop-months__labels" aria-hidden="true">
          {months.map((month) => (
            <span key={`${month.year}-${month.month}`}>{month.label}</span>
          ))}
        </div>
      </ChartFrame>
      <ChartReadout>{readout}</ChartReadout>
    </figure>
  );
}

export function SalesPage() {
  const { state, reload } = useShopView();
  const [open, setOpen] = useState<string | null>(null);
  if (state.view === null) return <ShopStatus state={state} reload={reload} />;
  const view = state.view;
  if (view.orders.length === 0) {
    return (
      <>
        <EmptyState title="Henüz sipariş verisi yok" text="Siparişler Etsy'den okununca burada listelenir." />
        <ConnectHint />
      </>
    );
  }
  const total = view.monthly.reduce((sum, month) => sum + month.cents, 0);
  const maxCountry = Math.max(1, ...view.countries.map((country) => country.orders));
  return (
    <>
      <section aria-labelledby="satis-aylik" className="section section--tight">
        <SectionHeader
          id="satis-aylik"
          title="Son 12 ay"
          right={
            <span className="section-header__aside">
              toplam <span className="mono">{formatUsd(centsToMicro(total), 0)}</span>
            </span>
          }
        />
        <MonthlyChart months={view.monthly} />
      </section>

      <section aria-labelledby="satis-ulke" className="section">
        <SectionHeader id="satis-ulke" title="Ülkeler" count={view.countries.length} />
        <ul className="shop-countries" aria-label="Ülkeye göre sipariş">
          {view.countries.slice(0, 12).map((country) => (
            <li key={country.code} className="shop-country">
              <span className="country mono">{country.code}</span>
              <span className="shop-country__track" aria-hidden="true">
                <span
                  className="shop-country__bar"
                  style={{ width: `${Math.max(2, Math.round((country.orders / maxCountry) * 100))}%` }}
                />
              </span>
              <span className="mono shop-country__n">
                {country.orders}
                {country.monthOrders > 0 ? (
                  <span className="shop-country__month"> · bu ay {country.monthOrders}</span>
                ) : null}
              </span>
              <span className="mono shop-country__rev">{formatUsd(centsToMicro(country.revenueCents), 0)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section aria-labelledby="satis-liste" className="section">
        <SectionHeader id="satis-liste" title="Siparişler" count={view.orders.length} />
        <ul className="shop-orders" aria-label="Siparişler">
          {view.orders.map((order) => (
            <OrderRow
              key={order.orderId}
              order={order}
              open={open === order.orderId}
              onToggle={() => setOpen(open === order.orderId ? null : order.orderId)}
            />
          ))}
        </ul>
      </section>
    </>
  );
}

// ---- İlanlar ----------------------------------------------------------------

type ListingFilter = 'aktif' | 'tumu';

const MONTH_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

/** Monthly visits, traffic sources and search terms of one listing, from the Etsy stats page read. */
function ListingStats({ listing, id }: { listing: ShopListing; id: string }) {
  const max = Math.max(1, ...listing.months.map((month) => month.visits));
  return (
    <div id={id} className="shop-listing__stats">
      {listing.months.length > 0 ? (
        <div className="shop-listing__months" role="img" aria-label="Aylık Etsy ziyaretleri">
          {listing.months.map((month) => (
            <span key={month.month} className="shop-listing__month" title={`${month.month}: ${month.visits} ziyaret`}>
              <span
                className="shop-listing__month-bar"
                style={{ height: `${Math.max(2, Math.round((month.visits / max) * 40))}px` }}
              />
              <span className="shop-listing__month-label">
                {MONTH_SHORT[Number(month.month.slice(5, 7)) - 1] ?? ''}
              </span>
              <span className="mono shop-listing__month-n">{month.visits}</span>
            </span>
          ))}
        </div>
      ) : null}
      {listing.traffic.length > 0 ? (
        <p className="shop-listing__line">
          <span className="shop-listing__label">Nereden</span>{' '}
          {listing.traffic
            .filter((source) => source.visits > 0)
            .map((source) => `${source.label} ${source.visits}`)
            .join(' · ')}
        </p>
      ) : null}
      {listing.terms.length > 0 ? (
        <p className="shop-listing__line">
          <span className="shop-listing__label">Aramalar</span>{' '}
          {listing.terms.map((term) => `${term.term} (${term.visits})`).join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

function ListingRow({ listing }: { listing: ShopListing }) {
  const active = listing.state === 'active';
  const [open, setOpen] = useState(false);
  const statsId = useId();
  const hasStats = listing.months.length > 0 || listing.traffic.length > 0 || listing.terms.length > 0;
  return (
    <li className={active ? 'shop-listing' : 'shop-listing shop-listing--off'}>
      <div className="shop-listing__head">
        {listing.url ? (
          <a className="shop-listing__title" href={listing.url} target="_blank" rel="noreferrer">
            {listing.title}
            <Icon name="external-link" size={12} />
          </a>
        ) : (
          <span className="shop-listing__title">{listing.title}</span>
        )}
        {listing.priceCents !== null ? (
          <span className="mono shop-listing__price">{formatUsd(centsToMicro(listing.priceCents), 0)}</span>
        ) : null}
      </div>
      <div className="shop-listing__meta">
        {active ? null : <span className="shop-listing__state">{listingStateLabel(listing.state)} · </span>}
        {listing.visits30 !== null ? (
          <>
            <span className="mono">{formatNumber(listing.visits30)}</span> ziyaret (30 gün) ·{' '}
          </>
        ) : null}
        {listing.viewsTotal !== null ? (
          <>
            <span className="mono">{formatNumber(listing.viewsTotal)}</span> görüntülenme (toplam
            {listing.views30 !== null ? (
              <>
                , <span className="mono">+{formatNumber(listing.views30)}</span> son 30 gün
              </>
            ) : null}
            ) ·{' '}
          </>
        ) : null}
        {listing.favorites !== null ? (
          <>
            <span className="mono">{formatNumber(listing.favorites)}</span> favori ·{' '}
          </>
        ) : null}
        <span className="mono">{listing.orders90}</span> sipariş ·{' '}
        <span className="mono">{formatUsd(centsToMicro(listing.revenue90Cents), 0)}</span> (90 gün)
        {hasStats ? (
          <>
            {' '}
            <button
              type="button"
              className="shop-listing__toggle"
              aria-expanded={open}
              aria-controls={open ? statsId : undefined}
              onClick={() => setOpen(!open)}
            >
              {open ? 'Gizle' : 'Ayrıntı'}
            </button>
          </>
        ) : null}
      </div>
      {open ? <ListingStats listing={listing} id={statsId} /> : null}
    </li>
  );
}

/** Etsy wide searches for the terms we follow (Marketplace Insights, read weekly in the browser). */
function MarketTerms({ terms }: { terms: ShopMarketTerm[] }) {
  return (
    <section aria-labelledby="ilan-arama" className="section">
      <SectionHeader id="ilan-arama" title="Etsy'de aranma (30 gün)" count={terms.length} />
      <div className="shop-market-wrap">
        <table className="shop-market" aria-label="Etsy arama hacmi">
          <thead>
            <tr>
              <th scope="col">Terim</th>
              <th scope="col">Arama</th>
              <th scope="col">Sonuç</th>
              <th scope="col">Dönüşüm</th>
            </tr>
          </thead>
          <tbody>
            {terms.map((term) => (
              <tr key={term.term}>
                <td>
                  {term.term}
                  {term.similar.length > 0 ? (
                    <span className="shop-market__similar">
                      benzer:{' '}
                      {term.similar
                        .slice(0, 3)
                        .map((item) => `${item.term} (${formatNumber(item.searches)})`)
                        .join(', ')}
                    </span>
                  ) : null}
                </td>
                <td className="mono">
                  {formatNumber(term.searches)}
                  {term.changePct !== null ? ` (${term.changePct > 0 ? '+' : ''}${term.changePct}%)` : ''}
                </td>
                <td className="mono">{term.results !== null ? formatNumber(term.results) : '·'}</td>
                <td>{term.conversion ?? '·'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Kontrol listesi B1 (28 Ekim 2026): an Etsy read now instead of waiting for the next three-hourly one. */
function SyncNow({ reload }: { reload: () => void }) {
  const { store } = useShop();
  const [status, setStatus] = useState<'idle' | 'busy' | 'queued' | 'failed'>('idle');
  const onClick = async () => {
    setStatus('busy');
    try {
      await store.requestSync();
      setStatus('queued');
      // The worker picks the request up within five minutes; the read itself takes about a minute.
      window.setTimeout(reload, 6 * 60_000);
    } catch {
      setStatus('failed');
    }
  };
  return (
    <span className="shop-sync">
      <button type="button" className="btn btn--secondary" disabled={status === 'busy'} onClick={() => void onClick()}>
        Şimdi senkronize et
      </button>
      {status === 'queued' ? <span role="status"> İstendi, birkaç dakika içinde okunur.</span> : null}
      {status === 'failed' ? <span role="status"> İstek gönderilemedi, tekrar dene.</span> : null}
    </span>
  );
}

export function ListingsPage({ now }: { now: number }) {
  const { state, reload } = useShopView();
  const [filter, setFilter] = useState<ListingFilter>('aktif');
  const [query, setQuery] = useState('');
  const searchId = useId();
  if (state.view === null) return <ShopStatus state={state} reload={reload} />;
  const view = state.view;
  if (view.listings.length === 0) {
    return (
      <>
        <EmptyState title="Henüz ilan verisi yok" text="İlanlar Etsy'den okununca burada listelenir." />
        <ConnectHint />
      </>
    );
  }
  const needle = query.trim().toLocaleLowerCase('tr-TR');
  const shown = view.listings.filter(
    (listing) =>
      (filter === 'tumu' || listing.state === 'active') &&
      (needle === '' ||
        listing.title.toLocaleLowerCase('tr-TR').includes(needle) ||
        listing.tags.some((tag) => tag.toLocaleLowerCase('tr-TR').includes(needle))),
  );
  return (
    <>
      <p className="shop-summary">
        <span className="mono">{view.listingCounts.active}</span> aktif ·{' '}
        <span className="mono">{view.listingCounts.total}</span> toplam
        {view.lastSyncedAt !== null ? (
          <>
            {' '}
            · son eşitleme <span className="mono">{formatRelativeClock(view.lastSyncedAt, now)}</span>
          </>
        ) : null}{' '}
        <SyncNow reload={reload} />
      </p>
      <div className="shop-filters">
        <Segmented<ListingFilter>
          label="İlan süzgeci"
          controls="ilan-liste"
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'aktif', label: 'Aktif', count: view.listingCounts.active },
            { id: 'tumu', label: 'Tümü', count: view.listingCounts.total },
          ]}
        />
        <label className="shop-search">
          <span className="sr-only" id={searchId}>
            İlan ara
          </span>
          <input
            type="search"
            aria-labelledby={searchId}
            placeholder="İlan ara"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </label>
      </div>
      <div id="ilan-liste" role="tabpanel" aria-label="İlan listesi">
        <ul className="shop-listings" aria-label="İlanlar">
          {shown.map((listing) => (
            <ListingRow key={listing.listingId} listing={listing} />
          ))}
        </ul>
        {shown.length === 0 ? <p className="shop-hint">Bu süzgeçle ilan yok.</p> : null}
      </div>
      {view.marketTerms.length > 0 ? <MarketTerms terms={view.marketTerms} /> : null}
    </>
  );
}
